"use client";

import React, { useState, useCallback } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Calendar as CalendarIcon } from "lucide-react";
import { format, parse } from "date-fns";
import { cn } from "@/lib/utils";

export interface CalendarDateEditModalProps {
  value: string | null | undefined;
  onValueChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Calendar-based date editor for modal/detail view editing
 * Provides a visual calendar picker with manual date input fallback
 * Designed for use in detail modals and forms
 */
export function CalendarDateEditModal({
  value,
  onValueChange,
  placeholder = "Select date...",
  autoFocus = false,
  className,
}: CalendarDateEditModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const [editValue, setEditValue] = useState(value || "");

  // Parse the current date value
  const currentDate = editValue
    ? parse(editValue, "yyyy-MM-dd", new Date())
    : value
      ? parse(String(value), "yyyy-MM-dd", new Date())
      : new Date();

  // Handle calendar date selection
  const handleDateSelect = useCallback(
    (date: Date | undefined) => {
      if (date) {
        const formattedDate = format(date, "yyyy-MM-dd");
        setEditValue(formattedDate);
        onValueChange(formattedDate);
        setInputError(null);
        setIsOpen(false);
      }
    },
    [onValueChange]
  );

  // Handle manual date input
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      setEditValue(inputValue);

      // Validate date format
      if (inputValue) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(inputValue)) {
          setInputError("Format: YYYY-MM-DD");
        } else {
          const parsedDate = parse(inputValue, "yyyy-MM-dd", new Date());
          if (isNaN(parsedDate.getTime())) {
            setInputError("Invalid date");
          } else {
            setInputError(null);
            onValueChange(inputValue);
          }
        }
      } else {
        setInputError(null);
        onValueChange("");
      }
    },
    [onValueChange]
  );

  // Handle clear button
  const handleClear = useCallback(() => {
    setEditValue("");
    setInputError(null);
    onValueChange("");
  }, [onValueChange]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
      }
    },
    []
  );

  const calendarPanel = (
    <div className="p-3">
      <DayPicker
        mode="single"
        selected={
          editValue ? parse(editValue, "yyyy-MM-dd", new Date()) : currentDate
        }
        onSelect={handleDateSelect}
        disabled={(date: Date) => {
          return false;
        }}
        initialFocus
      />
    </div>
  );

  return (
    // z-index-allow: detail modals are Mantine (default z-index 200, outside the
    // Tailwind token scale) — the calendar must outrank them, so this surface
    // uses z-max (9999). Mirrors the sibling inline editor CalendarDateEdit.
    <div className={cn("relative w-full z-max", className)}>
      {/* ResponsivePopover's desktop branch is a Mantine popover portalled to
          document.body at z-index 1100, so it still escapes the detail modal's
          stacking + overflow context AND outranks the modal itself (Mantine
          modals are z-index 200) — no z class needed on the panel. On mobile the
          picker becomes a drawer. Mirrors the sibling CalendarDateEdit. */}
      <ResponsivePopover forcePopover
        open={isOpen}
        onOpenChange={setIsOpen}
        content={calendarPanel}
        title="Select date"
        align="start"
        popoverSide="bottom"
        className="w-auto p-0"
        showCloseButton={false}
        asChild
      >
        <div
          className="flex items-center gap-1 w-full"
          // Controlled popover: the trigger owns opening (the Mantine target
          // injects no toggle when `open` is supplied). Value-form update (not
          // functional) so the calendar Button's own toggle, which bubbles here,
          // resolves to the same value instead of cancelling this one out.
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex-1 relative">
            <Input
              type="text"
              value={editValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={cn(
                "h-10 text-sm pr-8",
                inputError && "border-red-500 focus:border-red-500"
              )}
              autoFocus={autoFocus}
            />
            {editValue && (
              <button type="button"
                onClick={handleClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                title="Clear date"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-10 w-10 p-0"
            onClick={() => setIsOpen(!isOpen)}
            title="Open calendar"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </div>
      </ResponsivePopover>

      {inputError && (
        <div className="text-xs text-red-500 mt-1">{inputError}</div>
      )}
    </div>
  );
}
